<template>
  <div v-if="withSearch" class="popover-container">
    <div class="element-types-list">
      <FilterTypeRow
        v-for="element in filtersConfig"
        :key="element.id"
        :nr-of-elements="element?.optionList.length"
        :title="element.name"
        :is-selected="selectedFilterType === element.id"
        @on-select="handleSelectedType(element.id)"
      />
    </div>

    <Multiselect
      :selectedFilterType="selectedFilterType"
      :values="selectedValues[selectedFilterType]"
      :filtersConfig="filtersConfig"
      :fieldConfig="fieldConfig"
      :withSearch="withSearch"
      :unavailableFilterValues="unavailableFilterValues"
    >
      <component
        :is="getElementType(selectedFilterType)"
        :values="selectedValues[selectedFilterType]"
        :filtersConfig="filtersConfig"
        :fieldConfig="fieldConfig"
        @update:values="updateFilters"
      />
    </Multiselect>
  </div>

  <div v-else>
    <div class="filters-container">
      <div class="element-types-list">
        <FilterTypeRow
          v-for="(element, index) in filtersConfig"
          :key="element.id"
          :title="element.name"
          :is-selected="selectedFilterType === element.id"
          :applied-filter-text="getFilterText(element.id, index)"
          selected-position="left"
          @on-select="handleSelectedType(element.id)"
        />
      </div>

      <Multiselect
        :selectedFilterType="selectedFilterType"
        :values="selectedValues[selectedFilterType]"
        :filtersConfig="filtersConfig"
        :fieldConfig="fieldConfig"
        :withSearch="withSearch"
        :unavailableFilterValues="unavailableFilterValues"
      >
        <component
          :is="getElementType(selectedFilterType)"
          :values="selectedValues[selectedFilterType]"
          :filtersConfig="filtersConfig"
          :fieldConfig="fieldConfig"
          @update:values="updateFilters"
        />
      </Multiselect>
    </div>

    <div class="filters-footer">
      <div class="filters">
        <TextBody i18n-key="assist.filters.filters" :body-type="3" weight="medium" tag="span" />
        <div class="elements-list smaller-elements">
          <FilterLabel
            v-for="item in appliedFiltersArray"
            :key="item"
            :label="item"
            :is-selected="true"
            :with-clear-icon="true"
            @on-clear="onDeleteFilterValue(item)"
          />
        </div>
      </div>

      <div class="actions-container">
        <!--        <TextBody :text="numberOfActiveDocuments" :body-type="3" weight="medium" tag="span" />-->
        <BbBtn type="secondary" @click="onClearFilters">
          <TextBody i18n-key="common.reset" />
        </BbBtn>
        <BbBtn color="#6222E3">
          <TextBody i18n-key="assist.filters.applyFilters" />
        </BbBtn>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, toRaw, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { METADATA } from '~/constants/conversationModels';

import FilterTypeRow from '~/components/filters/FilterTypeRow.vue';
import useFormFilters from '~/composables/useFormFilters';
import components from '~/services/filter-form-service/components';
import Multiselect from '~/services/filter-form-service/components/MultiselectWrapper.vue';
import FilterLabel from '~/components/filters/FilterLabel.vue';
import TextBody from '~/components/common/TextBody.vue';
import useCollections from '~/composables/useCollections';
import useConversations from '~/composables/useConversations';

export default {
  components: {
    TextBody,
    FilterLabel,
    Multiselect,
    FilterTypeRow,
    ...components,
  },
  props: {
    filtersConfig: {
      type: Array,
      default: () => [],
      required: true,
    },
    allFilterValues: {
      type: Array,
      default: () => [],
      required: false,
    },
    withSearch: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: '', // e.g., 'document'
    },
  },
  setup(props) {
    const { t } = useI18n();
    const { setFiltersObject, formFilters } = useFormFilters();
    const { allCollections } = useCollections();
    const { conversationScope } = useConversations();

    const { filtersConfig, allFilterValues } = toRefs(props);

    const selectedFilterType = ref(METADATA.product_name);
    const selectedValues = ref({});

    const fieldConfig = computed(() => filtersConfig.value.find((item) => item.id === selectedFilterType.value) || {});

    const filterValues = computed(
      () => filtersConfig.value.find((item) => item.id === selectedFilterType.value)?.optionList || [],
    );

    const unavailableFilterValues = computed(
      () =>
        allFilterValues.value
          ?.find((item) => item.id === selectedFilterType.value)
          ?.optionList?.filter((option) => !filterValues.value.find((item) => item.label === option.label)) || [],
    );
    const appliedFiltersArray = computed(() => {
      const flatArray = Object.values(selectedValues.value).flat();

      return flatArray;
    });
    const numberOfActiveDocuments = computed(() => {
      const documentsInActiveCollection = conversationScope.value.collection.documents;
      const documentsInAllCollections = allCollections.value.flatMap((item) => item.documents);
      const numberOfDcuments =
        [...new Set(documentsInActiveCollection || [])].length ||
        [...new Set(documentsInAllCollections || [])].length ||
        0;

      return `Filtered to ${numberOfDcuments} documents`;
    });

    const getFilterText = (id, i) => {
      const v = Array.isArray(selectedValues.value[id]) ? toRaw(selectedValues.value[id]).filter((x) => x !== '') : [];
      return v.length ? `${v.length}${i === 0 ? ' ' + t('common.selected') : ''}` : '';
    };

    const getElementType = (id) => {
      const filter = filtersConfig.value.find((item) => item.id === id);

      return filter?.type;
    };

    const handleSelectedType = (type) => (selectedFilterType.value = type);
    const onClearFilters = () => {
      selectedValues.value = filtersConfig.value.reduce((acc, item) => {
        acc[item.id] = [];
        return acc;
      }, {});

      // Clear only filters for the current type
      setFiltersObject({ [props.type]: {} });
    };

    const onDeleteFilterValue = (value) => {
      const newValues = Object.fromEntries(
        Object.entries(selectedValues.value).map(([k, v]) => [k, v.filter((x) => x !== value)]),
      );

      selectedValues.value = newValues;

      setFiltersObject({ [props.type]: newValues });
    };

    const updateFilters = (value) => {
      const newValues = selectedValues.value[selectedFilterType.value]?.includes(value)
        ? selectedValues.value[selectedFilterType.value].filter((v) => v !== value)
        : [...(selectedValues.value[selectedFilterType.value] || []), value];

      selectedValues.value[selectedFilterType.value] = newValues;
      const filtered = Object.fromEntries(
        Object.entries(selectedValues.value).filter(([key, val]) => Array.isArray(val) && val.length > 0),
      );

      setFiltersObject({ [props.type]: filtered });
    };

    onMounted(() => {
      selectedValues.value = filtersConfig.value?.reduce((acc, item) => {
        acc[item.id] = [];
        return acc;
      }, {});

      if (formFilters.value[props.type]) {
        const typeFilters = formFilters.value[props.type];
        const formFiltersObj = Object.fromEntries(
          Object.entries(typeFilters).map(([key, value]) => [key, Array.isArray(value) ? value : [value]]),
        );
        selectedValues.value = { ...selectedValues.value, ...formFiltersObj };
      }
    });

    watch(filtersConfig, () => {
      selectedValues.value = filtersConfig.value?.reduce((acc, item) => {
        acc[item.id] = [];
        return acc;
      }, {});

      if (formFilters.value[props.type]) {
        const typeFilters = formFilters.value[props.type];
        const formFiltersObj = Object.fromEntries(
          Object.entries(typeFilters).map(([key, value]) => [key, Array.isArray(value) ? value : [value]]),
        );
        selectedValues.value = { ...selectedValues.value, ...formFiltersObj };
      }
    });

    watch(
      () => formFilters.value[props.type],
      (newVal) => {
        if (newVal) {
          selectedValues.value = { ...newVal };
        }
      },
      { deep: true },
    );

    return {
      t,
      onClearFilters,
      selectedValues,
      handleSelectedType,
      selectedFilterType,
      filterValues,
      updateFilters,
      getElementType,
      fieldConfig,
      unavailableFilterValues,
      onDeleteFilterValue,
      appliedFiltersArray,
      numberOfActiveDocuments,
      getFilterText,
    };
  },
};
</script>

<style scoped lang="scss">
.filters-container {
  display: flex;
  flex-direction: row;
  gap: $bb-spacing-6;
  padding: $bb-spacing-3 $bb-spacing-4;

  .element-types-list {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: $bb-spacing-1;

    :deep(.element-type) {
      background-color: $bb-neutral-6 !important;
      border-radius: 100px;
      height: 36px;

      div {
        font-size: 15px;
      }
    }

    :deep(.selected) {
      background-color: $bb-primary-1 !important;

      div {
        color: $bb-neutral-0 !important;
      }
    }

    :deep(.element-type .selected-label) {
      border: unset;
      background-color: unset;

      div {
        font-size: 17px;
        font-style: normal;
        font-weight: 500;
        line-height: 20px;
        text-overflow: unset;
        max-width: unset;
      }
    }
  }
}

.popover-container {
  display: flex;
  flex-direction: row;
  gap: $bb-spacing-6;
  width: 100%;

  .element-types-list {
    display: flex;
    flex-direction: column;
    margin: 25px 0 20px 20px;
    flex: 1;
  }

  .elements-list {
    display: flex;
    height: fit-content;
    gap: $bb-spacing-4;
    flex-wrap: wrap;
    flex-direction: row;
    margin: 30px 0 6px 0;
    padding: 0 $bb-spacing-4;
    flex: 2;
    overflow: scroll;
    max-height: 300px;
  }
}

.filters-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $bb-spacing-3 $bb-spacing-4 $bb-spacing-3 $bb-spacing-5;

  .filters {
    display: flex;
    align-items: center;
    gap: $bb-spacing-2;
  }

  .actions-container {
    display: flex;
    gap: $bb-spacing-3;
    align-items: center;
  }
}

.elements-list {
  display: flex;
  height: fit-content;
  gap: $bb-spacing-1;
  flex-wrap: wrap;
  flex-direction: row;

  :deep(.bb-btn.secondary) {
    border-radius: 100px !important;
    padding: 10px $bb-spacing-4 !important;
    height: $bb-spacing-9 !important;
  }

  :deep(.v-btn__content) {
    font-size: 12px !important;
    text-transform: capitalize;
  }

  :deep(.selected) {
    border: 1px solid $bb-primary-3 !important;
  }
}

.smaller-elements {
  :deep(.bb-btn.secondary) {
    padding: $bb-spacing-2 !important;
    height: $bb-spacing-6 !important;
  }

  :deep(.v-btn__content) {
    gap: $bb-spacing-1 !important;
  }
}
</style>
